
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseBreederListingsProps {
  breederId: string;
}

// Helper function to validate UUID format
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const useBreederListings = ({ breederId }: UseBreederListingsProps) => {
  return useQuery({
    queryKey: ['breeder-listings', breederId],
    queryFn: async () => {
      // Check if breederId is a valid UUID
      if (!breederId || !isValidUUID(breederId)) {
        console.log('Invalid breeder ID provided:', breederId);
        return { bulls: [], cows: [] };
      }

      const { data, error } = await supabase
        .from('listings')
        .select(`
          id,
          title,
          price,
          breed,
          type,
          location,
          images,
          price_currency,
          calving_ease,
          birth_weight,
          weaning_weight,
          milk,
          breeder_id,
          status,
          announcement_id,
          listing_type,
          created_at
        `)
        .eq('breeder_id', breederId)
        .in('status', ['active', 'upcoming'])
        .is('announcement_id', null) // Only get listings that are NOT part of announcements
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching breeder listings:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return { bulls: [], cows: [] };
      }

      // Get the breeder profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, business_name, first_name, last_name')
        .eq('id', breederId)
        .single();

      if (profileError) {
        console.error('Error fetching breeder profile:', profileError);
        throw profileError;
      }

      const breederName = profile?.business_name || 
                         `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 
                         'Unknown Breeder';

      // Transform the data to match the expected format
      const transformedData = data.map(listing => {
        // Handle images - convert from Json to string
        let imageUrl = "https://images.unsplash.com/photo-1493962853295-0fd70327578a?w=400&h=300&fit=crop";
        if (listing.images && Array.isArray(listing.images) && listing.images.length > 0) {
          imageUrl = String(listing.images[0]);
        }
        
        return {
          id: listing.id,
          title: listing.title,
          image: imageUrl,
          price: listing.price ? `${listing.price_currency || '$'}${listing.price.toLocaleString()}` : undefined,
          location: listing.location || 'Location not specified',
          breeder: breederName,
          breed: listing.breed,
          type: listing.type,
          listing_type: listing.listing_type || undefined,
          evaluationData: {
            calvingEase: listing.calving_ease,
            birthWeight: listing.birth_weight,
            weaningWeight: listing.weaning_weight,
            milk: listing.milk
          }
        };
      });

      // Separate bulls and cows
      const bulls = transformedData.filter(listing => 
        listing.type.toLowerCase().includes('bull')
      );
      
      const cows = transformedData.filter(listing => 
        listing.type.toLowerCase().includes('cow') && 
        !listing.type.toLowerCase().includes('bull')
      );

      return { bulls, cows };
    },
  });
};
